import { allCountries } from 'country-telephone-data';

const userType = [
    'SYSTEM',
    'HOSPITAL_STAFF',
    'TECHNICIAN'
  ];

  const userRoles = {
    SYSTEM : [ 'ROLE_DEFAULT'],
    TECHNICIAN: ['ROLE_BME'],
    HOSPITAL_STAFF: ['ROLE_REQUESTOR', 'ROLE_ADMINISTRATOR']
  }

  // Flat list of all available roles for direct selection in forms
  const allRoles = ['ROLE_BME', 'ROLE_REQUESTOR', 'ROLE_ADMINISTRATOR'];
  
  const permissions = {
    HOSPITAL_STAFF: [
      'ASSET_MANAGEMENT',
      'SERVICE_MANAGEMENT',
      'MAINTENANCE_MANAGEMENT',
      'HOSPITAL_USER_MANAGEMENT',
      'ADMINISTRATION_OPERATOR'
    ],
    TECHNICIAN: [
      'ADMINISTRATION_OPERATOR',
      'TECHNICIAN_SERVICE_MANAGEMENT',
      'TECHNICIAN_USER_MANAGEMENT'
    ]
  };

  const formatEnumName = (name) => {
  if (!name) return '';
  
  // Handle UPPER_SNAKE_CASE
  if (name === name.toUpperCase()) {
    return name.split('_')
      .map(word => word.charAt(0) + word.slice(1).toLowerCase())
      .join(' ');
  }
  
  // Handle camelCase
  return name
    .replace(/([A-Z])/g, ' $1')
    .replace(/^./, str => str.toUpperCase())
    .replace(/_/g, ' ');
};

   const formatCreatedDate = (dateStr) => {
    if (!dateStr) return null;

    const date = new Date(dateStr);

    // Check if the date is valid
    if (isNaN(date.getTime())) {
      console.warn('Invalid date string:', dateStr);
      return null;
    }

    // Always use DD/MM/YYYY format
    return date.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  /**
   * Formats a date/time to yyyy-MM-dd'T'HH:mm:ss.SSS format for API requests
   * @param {Date|string|number} date - The date to format (Date object, ISO string, or timestamp)
   * @returns {string} Formatted date string in yyyy-MM-dd'T'HH:mm:ss.SSS format
   */
  const formatDateTimeForAPI = (date) => {
    if (!date) return '';

    const dateObj = date instanceof Date ? date : new Date(date);

    // Check if date is valid
    if (isNaN(dateObj.getTime())) {
      console.warn('Invalid date provided to formatDateTimeForAPI:', date);
      return '';
    }

    const year = dateObj.getFullYear();
    const month = String(dateObj.getMonth() + 1).padStart(2, '0');
    const day = String(dateObj.getDate()).padStart(2, '0');
    const hours = String(dateObj.getHours()).padStart(2, '0');
    const minutes = String(dateObj.getMinutes()).padStart(2, '0');
    const seconds = String(dateObj.getSeconds()).padStart(2, '0');
    const milliseconds = String(dateObj.getMilliseconds()).padStart(3, '0');

    return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}.${milliseconds}`;
  };

  /**
   * Formats a date string from yyyy-MM-dd to dd-MM-yyyy for backend LocalDate fields
   * @param {string} dateStr - Date string in yyyy-MM-dd format
   * @returns {string} Formatted date string in dd-MM-yyyy format
   */
  const formatDateForAPI = (dateStr) => {
    if (!dateStr) return '';
    const [y, m, d] = dateStr.split('-');
    if (!y || !m || !d) return '';
    return `${d}-${m}-${y}`;
  };

  const dtPad = (n) => String(n).padStart(2, '0');

  // Current time as a datetime-local input value (browser-local wall clock).
  const nowLocalInputValue = () => {
    const d = new Date();
    return `${d.getFullYear()}-${dtPad(d.getMonth() + 1)}-${dtPad(d.getDate())}T${dtPad(d.getHours())}:${dtPad(d.getMinutes())}`;
  };

  // A datetime-local value is a wall-clock instant in the browser's timezone. The backend
  // stores/compares LocalDateTime in UTC, so send the UTC wall clock (no zone suffix) — otherwise
  // a browser ahead of UTC turns "now" into a future time and the action gets scheduled, not run.
  const toBackendDateTime = (localValue) => {
    if (!localValue) return null;
    const d = new Date(localValue);
    return `${d.getUTCFullYear()}-${dtPad(d.getUTCMonth() + 1)}-${dtPad(d.getUTCDate())}T${dtPad(d.getUTCHours())}:${dtPad(d.getUTCMinutes())}:${dtPad(d.getUTCSeconds())}`;
  };

  // API datetime strings arrive already converted to the requester's timezone — the api helper
  // sends X-Timezone on every request and the backend serializes responses in that zone. So for
  // a datetime-local input the value is already browser-local wall clock: truncate, no zone math.
  const apiDateTimeToInputValue = (apiString) => {
    if (!apiString) return '';
    return apiString.slice(0, 16); // yyyy-MM-ddTHH:mm
  };

 const formatCamelCase = (key) =>
  key
    .replace(/([A-Z])/g, ' $1')     // insert space before uppercase letters
    .replace(/^./, (str) => str.toUpperCase()); // capitalize the first letter


const countryOptions = allCountries.map(country => ({
  value: country.iso2,
  label: (
    <div className="d-flex align-items-center">
      <img 
        src={`https://flagcdn.com/16x12/${country.iso2.toLowerCase()}.png`} 
        alt={country.name} 
        style={{ marginRight: '8px', width: '16px' }}
      />
      {`${country.name} (${country.dialCode})`}
    </div>
  ),
  labelText: `${country.name} (${country.dialCode})`,  // <-- add this
  dialCode: country.dialCode,
  name: country.name
}));

  const statusOptions = [
    "PENDING",
    "REJECTED",
    "APPROVED",
    "INPROGRESS"
  ];

// Format user type for display
const formatUserTypeName = (userType) => {
  if (!userType) return '';

  // Special case for HOSPITAL_STAFF (Organization Members)
  if (userType === 'HOSPITAL_STAFF') return 'Organization Members';

  // Convert to title case for other types
  return userType
    .toLowerCase()
    .split('_')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

    // utils.js
const formatRoleName = (role, t) => {
  if (!role) return '';

  // Remove ROLE_ prefix if present
  const cleanRole = role.replace(/^ROLE_/, '');

  // Special case for DEFAULT
  if (cleanRole === 'DEFAULT') return t('shared.role_default', 'Default User')

  // Special case for ADMINISTRATOR (new consolidated admin role)
  if (cleanRole === 'ADMINISTRATOR') return t('shared.role_administrator', 'Administrator')

  // Special case for BME (Full User - renamed from Standard User)
  if (cleanRole === 'BME') return t('shared.role_full_user', 'Full User')

  // Special case for REQUESTOR (Requester)
  if (cleanRole === 'REQUESTOR') return t('shared.role_requester', 'Requester')

  // Legacy roles - display as Administrator during transition
  if (['COO', 'BMD_HOD', 'HOD', 'HOSPITAL_ADMIN'].includes(cleanRole))
    return t('shared.role_administrator', 'Administrator')

  // Convert to title case for unknown roles
  return cleanRole
    .toLowerCase()
    .split('_')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

// i18n static references for dynamic keys
const i18nDummyKeys = (t) => [
  t('shared.normal_wear', 'Normal Wear'),
  t('shared.operator_error', 'Operator Error'),
  t('shared.vandalism', 'Vandalism'),
  t('shared.breakdown', 'Breakdown'),
  t('shared.maintenance', 'Maintenance'),
  t('shared.submitted', 'Submitted'),
  t('shared.pending', 'Pending'),
  t('shared.in_progress', 'In Progress'),
  t('shared.on_hold', 'On Hold'),
  t('shared.resolved', 'Resolved'),
];

const currencies = (t) => ({
    "USD": `USD - ${t('shared.currency_usd', 'US Dollar')}`,
    "EUR": `EUR - ${t('shared.currency_eur', 'Euro')}`,
    "GBP": `GBP - ${t('shared.currency_gbp', 'British Pound')}`,
    "INR": `INR - ${t('shared.currency_inr', 'Indian Rupee')}`,
    "JPY": `JPY - ${t('shared.currency_jpy', 'Japanese Yen')}`,
    "CAD": `CAD - ${t('shared.currency_cad', 'Canadian Dollar')}`,
    "AUD": `AUD - ${t('shared.currency_aud', 'Australian Dollar')}`,
    "CHF": `CHF - ${t('shared.currency_chf', 'Swiss Franc')}`,
    "CNY": `CNY - ${t('shared.currency_cny', 'Chinese Yuan')}`,
    "SGD": `SGD - ${t('shared.currency_sgd', 'Singapore Dollar')}`,
    "HKD": `HKD - ${t('shared.currency_hkd', 'Hong Kong Dollar')}`,
    "SEK": `SEK - ${t('shared.currency_sek', 'Swedish Krona')}`,
    "NOK": `NOK - ${t('shared.currency_nok', 'Norwegian Krone')}`,
    "DKK": `DKK - ${t('shared.currency_dkk', 'Danish Krone')}`,
    "NZD": `NZD - ${t('shared.currency_nzd', 'New Zealand Dollar')}`,
    "ZAR": `ZAR - ${t('shared.currency_zar', 'South African Rand')}`,
    "BRL": `BRL - ${t('shared.currency_brl', 'Brazilian Real')}`,
    "MXN": `MXN - ${t('shared.currency_mxn', 'Mexican Peso')}`,
    "RUB": `RUB - ${t('shared.currency_rub', 'Russian Ruble')}`,
    "KRW": `KRW - ${t('shared.currency_krw', 'South Korean Won')}`,
    "TRY": `TRY - ${t('shared.currency_try', 'Turkish Lira')}`,
    "PLN": `PLN - ${t('shared.currency_pln', 'Polish Zloty')}`,
    "CZK": `CZK - ${t('shared.currency_czk', 'Czech Koruna')}`,
    "HUF": `HUF - ${t('shared.currency_huf', 'Hungarian Forint')}`,
    "ILS": `ILS - ${t('shared.currency_ils', 'Israeli Shekel')}`,
    "AED": `AED - ${t('shared.currency_aed', 'UAE Dirham')}`,
    "SAR": `SAR - ${t('shared.currency_sar', 'Saudi Riyal')}`,
    "QAR": `QAR - ${t('shared.currency_qar', 'Qatari Riyal')}`,
    "KWD": `KWD - ${t('shared.currency_kwd', 'Kuwaiti Dinar')}`,
    "BHD": `BHD - ${t('shared.currency_bhd', 'Bahraini Dinar')}`,
    "OMR": `OMR - ${t('shared.currency_omr', 'Omani Rial')}`,
    "EGP": `EGP - ${t('shared.currency_egp', 'Egyptian Pound')}`,
    "MAD": `MAD - ${t('shared.currency_mad', 'Moroccan Dirham')}`,
    "NGN": `NGN - ${t('shared.currency_ngn', 'Nigerian Naira')}`,
    "KES": `KES - ${t('shared.currency_kes', 'Kenyan Shilling')}`,
    "GHS": `GHS - ${t('shared.currency_ghs', 'Ghanaian Cedi')}`,
    "THB": `THB - ${t('shared.currency_thb', 'Thai Baht')}`,
    "MYR": `MYR - ${t('shared.currency_myr', 'Malaysian Ringgit')}`,
    "IDR": `IDR - ${t('shared.currency_idr', 'Indonesian Rupiah')}`,
    "PHP": `PHP - ${t('shared.currency_php', 'Philippine Peso')}`,
    "VND": `VND - ${t('shared.currency_vnd', 'Vietnamese Dong')}`,
    "PKR": `PKR - ${t('shared.currency_pkr', 'Pakistani Rupee')}`,
    "BDT": `BDT - ${t('shared.currency_bdt', 'Bankladeshi Taka')}`,
    "LKR": `LKR - ${t('shared.currency_lkr', 'Sri Lankan Rupee')}`,
    "NPR": `NPR - ${t('shared.currency_npr', 'Nepalese Rupee')}`,
    "AFN": `AFN - ${t('shared.currency_afn', 'Afghan Afghani')}`,
    "IRR": `IRR - ${t('shared.currency_irr', 'Iranian Rial')}`,
    "IQD": `IQD - ${t('shared.currency_iqd', 'Iraqi Dinar')}`,
    "JOD": `JOD - ${t('shared.currency_jod', 'Jordanian Dinar')}`,
    "LBP": `LBP - ${t('shared.currency_lbp', 'Lebanese Pound')}`,
    "SYP": `SYP - ${t('shared.currency_syp', 'Syrian Pound')}`,
    "ARS": `ARS - ${t('shared.currency_ars', 'Argentine Peso')}`,
    "CLP": `CLP - ${t('shared.currency_clp', 'Chilean Peso')}`,
    "COP": `COP - ${t('shared.currency_cop', 'Colombian Peso')}`,
    "PEN": `PEN - ${t('shared.currency_pen', 'Peruvian Sol')}`,
    "UYU": `UYU - ${t('shared.currency_uyu', 'Uruguayan Peso')}`,
    "BOB": `BOB - ${t('shared.currency_bob', 'Bolivian Boliviano')}`,
    "PYG": `PYG - ${t('shared.currency_pyg', 'Paraguayan Guarani')}`,
    "VES": `VES - ${t('shared.currency_ves', 'Venezuelan Bolívar')}`
  });;

const getStatusBadgeClass = (status) => {
  switch (status) {
    case "APPROVED":
      return "bg-success";
    case "REJECTED":
      return "bg-danger";
    case "PENDING":
      return "bg-warning text-dark";
    case "INPROGRESS":
      return "bg-primary";
    default:
      return "bg-secondary";
  }
};

/**
 * Returns the Bootstrap badge color for a given work type
 * @param {string} workType - The work type (PREVENTIVE, REACTIVE, etc.)
 * @returns {string} Bootstrap color name for the Badge component
 */
const getWorkTypeBadgeColor = (workType) => {
  switch (workType?.toUpperCase()) {
    case 'PREVENTIVE': return 'warning';    // yellow
    case 'REACTIVE': return 'primary';      // blue
    default: return 'secondary';            // gray
  }
};

const getStatusDescription = (status) => {
  switch (status) {
    case "APPROVED":
      return "User verification is complete and approved";
    case "REJECTED":
      return "User verification has been rejected";
    case "PENDING":
      return "Waiting for verification review";
    case "INPROGRESS":
      return "Verification is currently being processed";
    default:
      return "Status not specified";
  }
};

const urgencyColors = {
  HIGH: "bg-danger",
  MEDIUM: "bg-warning text-white",
  LOW: "bg-success",
  SEVERE: "bg-dark",
  NONE: "bg-secondary",
  "": "bg-secondary", // nothing at all
  null: "bg-secondary",
  undefined: "bg-secondary",
};

const workstatusOptions = (t) => [
  {
    label: t('shared.open', 'Open'),
    icon: 'file-plus',
    value: 'SUBMITTED',
    colorClass: 'bg-info text-white',
    borderColor: '#0dcaf0',
  },
  {
    label: t('shared.in_progress', 'In Progress'),
    icon: 'clock',
    value: 'IN_PROGRESS',
    colorClass: 'bg-warning text-white',
    borderColor: '#ffc107',
  },
  {
    label: t('shared.resolved', 'Resolved'),
    icon: 'check-circle',
    value: 'RESOLVED',
    colorClass: 'bg-success text-white',
    borderColor: '#28a745',
  },
  {
    label: t('shared.on_hold', 'On Hold'),
    icon: 'pause-circle',
    value: 'ON_HOLD',
    colorClass: 'bg-danger text-white',
    borderColor: '#dc3545',
  },
 {
  label: t('shared.pending', 'Pending'),
  icon: 'user-check',
  value: 'PENDING',
  colorClass: 'bg-primary text-white',
  borderColor: '#0d6efd',
},
{
  label: t('shared.open', 'Open'),
  icon: 'file',
  value: 'OPEN',
  colorClass: 'bg-info text-white',
  borderColor: '#17a2b8',
},
{
  label: t('shared.completed', 'Completed'),
  icon: 'check-circle',
  value: 'COMPLETED',
  colorClass: 'bg-success text-white',
  borderColor: '#28a745',
},
{
  label: t('shared.in_process', 'In Process'),
  icon: 'loader',
  value: 'IN_PROCESS',
  colorClass: 'bg-warning text-dark',
  borderColor: '#ffc107',
},
{
  label: t('shared.cancelled', 'Cancelled'),
  icon: 'x-circle',
  value: 'CANCELLED',
  colorClass: 'bg-danger text-white',
  borderColor: '#dc3545',
}

];

// Reactive work order status options - only includes the 5 valid backend statuses
const reactiveStatusOptions = (t) => ([
  {
    label: t('shared.open', 'Open'),
    icon: 'file-plus',
    value: 'SUBMITTED',
    colorClass: 'bg-info text-white',
    borderColor: '#0dcaf0',
  },
  {
    label: t('shared.pending', 'Pending'),
    icon: 'user-check',
    value: 'PENDING',
    colorClass: 'bg-primary text-white',
    borderColor: '#0d6efd',
  },
  {
    label: t('shared.in_progress', 'In Progress'),
    icon: 'clock',
    value: 'IN_PROGRESS',
    colorClass: 'bg-warning text-white',
    borderColor: '#ffc107',
  },
  {
    label: t('shared.on_hold', 'On Hold'),
    icon: 'pause-circle',
    value: 'ON_HOLD',
    colorClass: 'bg-danger text-white',
    borderColor: '#dc3545',
  },
  {
    label: t('shared.resolved', 'Resolved'),
    icon: 'check-circle',
    value: 'RESOLVED',
    colorClass: 'bg-success text-white',
    borderColor: '#28a745',
  },
]);

const assetStatusOptions = (t) => [
  { value: "ONLINE", label: t("shared.online", "Online"), color: "success",
    hint: t("shared.asset_is_operational", "Asset is operational and available for use") },
  { value: "OFFLINE", label: t("shared.offline", "Offline"), color: "danger",
    hint: t("shared.asset_is_offline_hint", "Asset is offline (select downtime reason when changing status)") },
  { value: "DO_NOT_TRACK", label: t("shared.do_not_track", "Do Not Track"), color: "secondary",
    hint: t("shared.asset_is_not_tracked", "Asset is not actively tracked for availability metrics") },
  { value: "DECOMMISSIONED", label: t("shared.decommissioned", "Decommissioned"), color: "dark",
    hint: t("shared.asset_is_decommissioned", "Asset has been permanently retired from service") },
];

const groupedAssetStatusOptions = (t) => [
  {
    group: t("shared.online", "Online"),
    options: [
      { value: "ONLINE", label: t("shared.online", "Online"), color: "#10b981", hint: t("shared.asset_is_operational", "Asset is operational and available for use") },
    ]
  },
  {
    group: t("shared.offline", "Offline"),
    options: [
      { value: "OFFLINE", label: t("shared.offline", "Offline"), color: "#ef4444", hint: t("shared.asset_is_offline_hint", "Asset is offline (select downtime reason when changing status)") },
    ]
  },
  {
    group: t("shared.not_tracked", "Not Tracked"),
    options: [
      { value: "DO_NOT_TRACK", label: t("shared.do_not_track", "Do Not Track"), color: "#6b7280", hint: t("shared.asset_is_not_tracked", "Asset is not actively tracked for availability metrics") },
    ]
  },
  {
    group: t("shared.decommissioned", "Decommissioned"),
    options: [
      { value: "DECOMMISSIONED", label: t("shared.decommissioned", "Decommissioned"), color: "#475569", hint: t("shared.asset_is_decommissioned", "Asset has been permanently retired from service") },
    ]
  }
];

/**
 * Maximum number of assets one bulk location transfer may move. Mirrors the backend's
 * AssetLocationTransferRequest.MAX_BULK_ASSET_IDS — the executor relocates each asset's whole
 * sub-tree and writes an activity row per moved asset, so an unbounded selection is a timeout risk.
 * The selection UI caps at this value so the user is told before submitting rather than after.
 */
export const BULK_TRANSFER_MAX_ASSETS = 200;

export const TIMEZONE_MAP = {
  // UTC/GMT
  "UTC": "UTC",
  "GMT": "GMT",

  // North America
  "America/New_York": "EST",
  "America/Chicago": "CST",
  "America/Denver": "MST",
  "America/Los_Angeles": "PST",
  "America/Anchorage": "AKST",
  "Pacific/Honolulu": "HST",
  "America/Halifax": "AST",
  "America/St_Johns": "NST",

  // Europe
  "Europe/Lisbon": "WET",
  "Europe/Paris": "CET",
  "Europe/Athens": "EET",
  "Europe/London": "BST",
  "Europe/Dublin": "IST_IRELAND",

  // Asia
  "Asia/Kolkata": "IST",
  "Asia/Calcutta": "IST", // Alias for Asia/Kolkata
  "Asia/Karachi": "PKT",
  "Asia/Dhaka": "BST_BANGLADESH",
  "Asia/Kathmandu": "NPT",
  "Asia/Thimphu": "BTT",
  "Asia/Colombo": "LKT",
  "Indian/Maldives": "MVT",
  "Asia/Bangkok": "ICT",
  "Asia/Jakarta": "WIB",
  "Asia/Makassar": "WITA",
  "Asia/Jayapura": "WIT",
  "Asia/Singapore": "SGT",
  "Asia/Kuala_Lumpur": "MYT",
  "Asia/Manila": "PHT",
  "Asia/Hong_Kong": "HKT",
  "Asia/Shanghai": "CST_CHINA",
  "Asia/Tokyo": "JST",
  "Asia/Seoul": "KST",
  "Australia/Sydney": "AEST",
  "Australia/Adelaide": "ACST",
  "Australia/Perth": "AWST",

  // Middle East
  "Asia/Dubai": "GST",
  "Asia/Riyadh": "AST_ARABIA",
  "Asia/Tehran": "IRST",
  "Europe/Moscow": "MSK",

  // Africa
  "Africa/Lagos": "WAT",
  "Africa/Harare": "CAT",
  "Africa/Nairobi": "EAT",
  "Africa/Johannesburg": "SAST",

  // South America
  "America/Sao_Paulo": "BRT",
  "America/Argentina/Buenos_Aires": "ART",
  "America/Santiago": "CLT",
  "America/Bogota": "COT",
  "America/Lima": "PET",
  "America/Caracas": "VET",

  // Pacific
  "Pacific/Auckland": "NZST",
  "Pacific/Fiji": "FJT",
  "Pacific/Tongatapu": "TOT",
  "Pacific/Apia": "WST_SAMOA",

  // Other
  "Atlantic/Azores": "AZOST",
  "Atlantic/Cape_Verde": "CVT"
};

/**
 * Get the user's browser timezone and map it to the backend timezone format
 * @returns {string} Mapped timezone code or default "UTC"
 */
export const getUserTimezone = () => {
  try {
    const browserTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    return TIMEZONE_MAP[browserTimezone] || browserTimezone || "UTC";
  } catch (error) {
    console.warn("Failed to detect timezone:", error);
    return "UTC";
  }
};

/**
 * Handles API responses that may have warnings but still return 200 status
 * @param {Object} response - The API response object
 * @param {Function} onSuccess - Callback for successful operations
 * @param {Function} onWarning - Callback for operations with warnings (optional)
 * @param {Function} onError - Callback for error handling (optional)
 * @returns {Object} - Processed response with success flag and messages
 */
const handleApiResponse = (response, onSuccess = null, onWarning = null, onError = null) => {
  const { status, message, warning, error, response: data } = response;
  
  // Handle successful response (status 200)
  if (status === 200) {
    // Check for warnings
    if (warning && warning.length > 0) {
      const warningMessage = Array.isArray(warning) ? warning.join(', ') : warning;
      
      if (onWarning) {
        onWarning(warningMessage, data);
      }
      
      return {
        success: true,
        hasWarnings: true,
        message: message || 'Operation completed with warnings',
        warnings: warning,
        data: data
      };
    }
    
    // No warnings, fully successful
    if (onSuccess) {
      onSuccess(data);
    }
    
    return {
      success: true,
      hasWarnings: false,
      message: message || 'Operation completed successfully',
      data: data
    };
  }
  
  // Handle error responses
  const errorMessage = error || message || 'Operation failed';
  
  if (onError) {
    onError(errorMessage);
  }
  
  return {
    success: false,
    hasWarnings: false,
    message: errorMessage,
    error: error
  };
};

/**
 * Creates a notification message based on API response
 * @param {Object} apiResult - Result from handleApiResponse
 * @param {string} successMessage - Custom success message (optional)
 * @param {string} warningPrefix - Prefix for warning messages (optional)
 * @returns {Object} - Notification object with type and message
 */
const createNotificationFromApiResponse = (apiResult, successMessage = null, warningPrefix = '') => {
  if (!apiResult.success) {
    return {
      type: 'error',
      message: apiResult.message
    };
  }
  
  if (apiResult.hasWarnings) {
    const warningMessage = warningPrefix 
      ? `${warningPrefix}: ${apiResult.warnings.join(', ')}` 
      : `Warning: ${apiResult.warnings.join(', ')}`;
      
    return {
      type: 'warning',
      message: warningMessage
    };
  }
  
  return {
    type: 'success',
    message: successMessage || apiResult.message
  };
};

  const reactiveEnumTranslations = (t) => ({
    // Impact
    "NO IMPACT": t('shared.no_impact', 'No impact'),
    "DELAYED CARE": t('shared.delayed_care', 'Delayed care'),
    "STOPPED CARE": t('shared.stopped_care', 'Stopped care'),
    "SAFETY RISK": t('shared.safety_risk', 'Safety risk'),
    // Problem Type
    "BREAKDOWN": t('shared.breakdown', 'Breakdown'),
    "NOISE": t('shared.noise', 'Noise'),
    "DISPLAY ERROR": t('shared.display_error', 'Display error'),
    "CALIBRATION": t('shared.calibration', 'Calibration'),
    "PHYSICAL DAMAGE": t('shared.physical_damage', 'Physical damage'),
    "ALARM MALFUNCTION": t('shared.alarm_malfunction', 'Alarm malfunction'),
    "SOFTWARE CRASH": t('shared.software_crash', 'Software crash'),
    "POWER FAILURE": t('shared.power_failure', 'Power failure'),
    "OTHER": t('shared.other', 'Other'),
    "PREVENTIVE MAINTENANCE": t('shared.preventive_maintenance', 'Preventive Maintenance'),
    // Symptoms
    "NO POWER": t('shared.no_power', 'No power'),
    "LEAK": t('shared.leak', 'Leak'),
    "UNRESPONSIVE SCREEN": t('shared.unresponsive_screen', 'Unresponsive screen'),
    "HEAT": t('shared.heat', 'Heat'),
    "ERROR CODE ON DISPLAY": t('shared.error_code_on_display', 'Error code on display'),
    "ALARM TRIGGERED": t('shared.alarm_triggered', 'Alarm triggered'),
    "INTERMITTENT ISSUE": t('shared.intermittent_issue', 'Intermittent issue'),
    // Priority
    "LOW": t('shared.low', 'Low'),
    "MEDIUM": t('shared.medium', 'Medium'),
    "HIGH": t('shared.high', 'High'),
    // Usage Status
    "ONLINE": t('shared.online', 'Online'),
    "OFFLINE": t('shared.offline', 'Offline'),
    "DO_NOT_TRACK": t('shared.do_not_track', 'Do Not Track'),
    "IN_USE": t('shared.in_use', 'In Use')
  });
  
  const vendorTypeTranslations = (t) => ({
    'SERVICE_PROVIDER': t('shared.service_provider', 'Service Provider'),
    'SPARE_PARTS_SUPPLIER': t('shared.spare_parts_supplier', 'Spare Parts Supplier'),
    'OEM': t('shared.oem', 'OEM'),
    'DISTRIBUTOR': t('shared.distributor', 'Distributor')
  });

  export {
    userType,
    permissions,
    formatCreatedDate,
    formatDateTimeForAPI,
    formatDateForAPI,
    nowLocalInputValue,
    toBackendDateTime,
    apiDateTimeToInputValue,
    formatEnumName,
    formatRoleName,
    formatUserTypeName,
    formatCamelCase,
    countryOptions,
    userRoles,
    allRoles,
    statusOptions,
    getStatusBadgeClass,
    getStatusDescription,
    getWorkTypeBadgeColor,
    workstatusOptions,
    reactiveStatusOptions,
    urgencyColors,
    assetStatusOptions,
    groupedAssetStatusOptions,
    currencies,
    handleApiResponse,
    createNotificationFromApiResponse,
    reactiveEnumTranslations,
    vendorTypeTranslations
  };
  